(() => {
  if (customElements.get('solstice-delivery-estimate')) return;

  const DAY = 86400000;
  const DEADLINES = [12, 20, 23];
  const parisClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });
  const dateLabels = Object.fromEntries(['fr', 'en', 'de', 'es', 'fi', 'hu', 'it', 'nl', 'pl', 'sv'].map(locale => [locale, new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale, { timeZone: 'UTC', day: 'numeric', month: 'long' })]));

  function parisParts(date) {
    return Object.fromEntries(parisClock.formatToParts(date)
      .filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
  }

  // Convert a Paris calendar date and hour to an instant, including DST changes.
  function deadlineInstant(calendarDay, hour) {
    const wallTime = calendarDay + hour * 3600000;
    let instant = wallTime;
    for (let i = 0; i < 2; i++) {
      const parts = parisParts(new Date(instant));
      const localTime = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      instant += wallTime - localTime;
    }
    return instant;
  }

  function getEstimate(now = new Date(), country = 'FR', language = 'fr') {
    const parts = parisParts(now);
    const today = Date.UTC(parts.year, parts.month - 1, parts.day);
    let orderDay = today;
    let deadline = DEADLINES.map(hour => deadlineInstant(today, hour)).find(value => value > now.getTime());
    if (!deadline) {
      orderDay += DAY;
      deadline = deadlineInstant(orderDay, DEADLINES[0]);
    }

    // Four days in France, eight elsewhere, excluding Sundays.
    let deliveryDay = orderDay;
    let remainingDays = country === 'FR' ? 4 : 8;
    while (remainingDays > 0) {
      deliveryDay += DAY;
      if (new Date(deliveryDay).getUTCDay() !== 0) remainingDays--;
    }
    const remaining = deadline - now.getTime();
    const totalSeconds = Math.ceil(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor(totalSeconds % 3600 / 60);
    const seconds = totalSeconds % 60;
    const pad = value => String(value).padStart(2, '0');
    const countdown = `${pad(hours)} h ${pad(minutes)} min ${pad(seconds)} s`;
    return { days: (deliveryDay - today) / DAY, date: (dateLabels[language.toLowerCase().split('-')[0]] || dateLabels.en).format(deliveryDay), countdown, deadline };
  }

  window.SolsticeDeliveryEstimate = { getEstimate };
  const instances = new Set();
  let timer;
  const refresh = () => {
    const now = new Date();
    instances.forEach(element => element.update(now));
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.daysNode = this.querySelector('[data-delivery-days]');
      this.dateNode = this.querySelector('[data-delivery-date]');
      this.countdownNode = this.querySelector('[data-delivery-countdown]');
      if (!this.daysNode || !this.dateNode || !this.countdownNode) return;
      this.scope = this.closest('composition-catalog-product, product-info');
      if (this.dataset.button && this.scope) {
        this.observer = new MutationObserver(() => this.update());
        this.observer.observe(this.scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
      }
      instances.add(this);
      this.update();
      if (!timer) {
        timer = setInterval(refresh, 1000);
        window.addEventListener('pageshow', refresh);
        document.addEventListener('visibilitychange', refresh);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      instances.delete(this);
      if (!instances.size) {
        clearInterval(timer);
        timer = null;
        window.removeEventListener('pageshow', refresh);
        document.removeEventListener('visibilitychange', refresh);
      }
    }

    update(now = new Date()) {
      const button = this.dataset.button && this.scope?.querySelector(this.dataset.button);
      this.hidden = !!this.dataset.button && (!button || button.disabled) || !!this.scope?.matches('[data-has-restock]') || !!this.scope?.querySelector('[id^=Delivery-] [data-delivery-state=restock]') || (!!this.scope?.matches('composition-catalog-product') && this.dataset.country === 'FR');
      if (this.hidden) return;
      const language = this.dataset.language || 'fr';
      const estimate = getEstimate(now, this.dataset.country || 'FR', language);
      const dayLabels = {"fr": {"Livraison dans {{ count }} jours": "Livraison dans {{ count }} jours"}, "en": {"Livraison dans {{ count }} jours": "Delivery in {{ count }} days"}, "de": {"Livraison dans {{ count }} jours": "Lieferung in {{ count }} Tagen"}, "es": {"Livraison dans {{ count }} jours": "Entrega en {{ count }} días"}, "fi": {"Livraison dans {{ count }} jours": "Toimitus {{ count }} päivässä"}, "hu": {"Livraison dans {{ count }} jours": "Szállítás {{ count }} napon belül"}, "it": {"Livraison dans {{ count }} jours": "Consegna in {{ count }} giorni"}, "nl": {"Livraison dans {{ count }} jours": "Levering binnen {{ count }} dagen"}, "pl": {"Livraison dans {{ count }} jours": "Dostawa: {{ count }} dni"}, "sv": {"Livraison dans {{ count }} jours": "Leverans om {{ count }} dagar"}};
      const daysText = (dayLabels[language.toLowerCase().split('-')[0]] || dayLabels.en)['Livraison dans {{ count }} jours'].replace('{{ count }}', estimate.days);
      const dateText = `(${estimate.date})`;
      if (this.daysNode.textContent !== daysText) this.daysNode.textContent = daysText;
      if (this.dateNode.textContent !== dateText) this.dateNode.textContent = dateText;
      if (this.countdownNode.textContent !== estimate.countdown) this.countdownNode.textContent = estimate.countdown;
    }
  }

  customElements.define('solstice-delivery-estimate', DeliveryEstimate);
})();
